import {Component, EventEmitter, Input, Output} from '@angular/core';
import {AbstractComponent, FileType} from "@components";
import {CompetitorService, LoaderService, ToastService} from "@services";
import {tap} from "rxjs";
import {TranslocoService} from "@ngneat/transloco";
import {Race, TOAST_STATE} from "@model";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";

@Component({
  selector: 'app-import-competitors-modal',
  templateUrl: './import-competitors-modal.component.html',
  styleUrl: './import-competitors-modal.component.scss'
})
export class ImportCompetitorsModalComponent extends AbstractComponent {

  @Input() race: Race | undefined;
  @Output() onSaveSuccessAction: EventEmitter<void> = new EventEmitter<void>();

  componentState: ImportCompetitorsModalState = ImportCompetitorsModalState.IMPORT;
  selectedFile: string | undefined;
  importType: ImportType = ImportType.APPEND;
  importTypes: ImportType[] = [ImportType.APPEND, ImportType.MERGE, ImportType.OVERWRITE]
  rowErrors: Map<string, [string, string][]> = new Map<string, [string, string][]>();

  constructor(private translocoService: TranslocoService, private toastService: ToastService, public activeModal: NgbActiveModal,
                private competitorService: CompetitorService, private loaderService: LoaderService) {
    super();
  }

  competitorFileAdded(file: string | undefined) {
    this.selectedFile = file;
  }

  onImportTypeChange(type: ImportType) {
    this.importType = type;
  }

  downloadTemplate() {
    //todo
  }

  onDismissClick() {
    this.activeModal.dismiss();
  }

  onSave() {
    this.loaderService.startLoading();
    this.competitorService.importFromExcel(this.race!.id!, this.base64ToFile(this.selectedFile!, "competitors.xlsx")).pipe(tap(
        () =>
            this.loaderService.dismissLoading())).subscribe((value)=>{
      if (value?.result && value.result.length > 0) {
        this.componentState = ImportCompetitorsModalState.ERROR;
        this.rowErrors = new Map<string, [string, string][]>();

        value.result.forEach(error => {
          const rowIndex = error.rowIndex.toString();

          const newErrors: [string, string][] = Object.entries(error.columnErrors)
              .map(([key, val]) => [key, val] as [string, string]);

          if (this.rowErrors.has(rowIndex)) {
            const existingErrors = this.rowErrors.get(rowIndex) || [];
            this.rowErrors.set(rowIndex, [...existingErrors, ...newErrors]);
          } else {
            this.rowErrors.set(rowIndex, newErrors);
          }
        });
      } else if (value.resultCode === -1) {
        this.componentState = ImportCompetitorsModalState.ERROR;
        this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("race.management.competitors.import.xls.error"));
        this.onDismissClick();
      } else {
        this.toastService.showToast(TOAST_STATE.success,
            this.translocoService.translate("race.management.competitors.import.xls.success"));
        this.onSaveSuccessAction.emit();
        this.onDismissClick();
      }
    });
  }

  getErrorMessages(columnErrors: Record<string, string>): string {
    return Object.entries(columnErrors)
        .map(([key, val]) => `${key} - ${val}`)
        .join(', ');
  }

  base64ToFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  get rowErrorKeys(): string[] {
    return Array.from(this.rowErrors.keys());
  }

  protected readonly FileType = FileType;
  protected readonly ImportCompetitorsModalState = ImportCompetitorsModalState;

}

enum ImportCompetitorsModalState {
  IMPORT,
  LOADING,
  SUCCESS,
  ERROR
}

export enum ImportType {
  OVERWRITE = 1,
  APPEND = 2,
  MERGE = 3
}
